// #region NotificationListener Component overview
/**
 * @fileoverview NotificationListener Component
 *
 * A React component that listens for real-time notifications via WebPubSub.
 * Handles document status updates and file upload notifications, displaying
 * them to the user through React-Admin's notification system.
 *
 * Features:
 * - Real-time document processing status updates
 * - File upload completion notifications
 * - Automatic token refresh handling
 * - Graceful fallback when user ID is unavailable
 *
 * @returns {null} This component does not render any UI elements
 */
// #endregion

// #region Imports
import { useNotify, useGetIdentity } from "react-admin";
import { useWebPubSub, WebPubSubMessage } from "../../hooks/useWebPubSub";
import { useCallback, useEffect } from "react";
import { featureFlags } from "../../utils/featureFlags";
import { setImperativeNotify } from "@/providers/notify/imperativeNotify";
import { AlertType } from "@/types";

const NotificationListener = () => {
  const notify = useNotify();
  const { data: identity } = useGetIdentity();

  // Bridge react-admin's `notify` to non-React callers (e.g. authProvider.checkError's
  // 403 handling). Registered for as long as the authenticated layout is mounted.
  useEffect(() => {
    setImperativeNotify(notify);
    return () => setImperativeNotify(null);
  }, [notify]);

  // Debug logging for feature flags
  console.log("[NotificationListener] Feature flags status:", {
    notificationsEnabled: featureFlags.notificationsEnabled,
    webPubSubEnabled: featureFlags.webPubSubEnabled,
  });

  // Extract and validate user ID from identity data
  // Accepts string or number ID, falls back to undefined if not present
  const userId =
    identity?.username && (typeof identity.username === "string" || typeof identity.username === "number")
      ? String(identity.username)
      : undefined;

  /**
   * Handles incoming WebPubSub messages and displays appropriate notifications
   *
   * @param message - The WebPubSub message containing notification data
   */
  const handleMessage = useCallback(
    (message: WebPubSubMessage) => {
      // Handle document processing status updates
      if (message.type === "document-status-update" && message.data) {
        notify(
          `Doc ${message.data.documentId || ""} status: ${message.data.status}\n` +
            `File: ${message.data.fileName || ""}\n` +
            (message.data.progress !== undefined ? `Progress: ${message.data.progress}%\n` : ""),
          { type: AlertType.INFO }
        );
      }

      // Handle file upload completion notifications
      if (message.type === "file-upload" && message.data) {
        notify(
          `File uploaded: ${message.data.fileName}\n` +
            `Size: ${message.data.fileSize ? (message.data.fileSize / 1024).toFixed(2) + " KB" : "N/A"}\n` +
            `User: ${message.data.userId || ""}\n` +
            (message.data.uploadTimestamp
              ? `Uploaded at: ${new Date(message.data.uploadTimestamp).toLocaleString()}`
              : ""),
          { type: AlertType.SUCCESS }
        );
      }
    },
    [notify]
  );

  // Initialize WebPubSub connection - hook is always called but completely inert when disabled
  // This prevents any connection attempts when WebPubSub is not explicitly enabled
  useWebPubSub({
    userId: featureFlags.webPubSubEnabled && userId ? userId : "",
    enabled: featureFlags.webPubSubEnabled && Boolean(userId),
    negotiationUrl: featureFlags.webPubSubEnabled
      ? import.meta.env.VITE_WEBPUBSUB_NEGOTIATION_URL || "http://localhost:9999/api/negotiate"
      : "",
    onMessage: handleMessage,
  });

  // Return null if user ID is missing or notifications are disabled
  if (!userId || !featureFlags.notificationsEnabled) {
    // Note: Could optionally show a warning notification here
    // notify("User ID is missing or invalid. Notifications are disabled.", { type: AlertType.WARNING });
    return null;
  }

  return null; // This component does not render any UI elements
};

export default NotificationListener;
